
// Ported from dates.ts - handle Excel date parsing and formatting

export function parseExcelDate(value, monthHint = null) {
    if (!value) return null;
    if (value instanceof Date) return value;
    
    if (typeof value === 'number') {
        const days = Math.floor(value);
        const timeFraction = value - days;
        const adjustedDays = days > 60 ? days - 1 : days;
        const baseDate = new Date(1899, 11, 31);
        const date = new Date(baseDate.getTime() + adjustedDays * 86400000);
        if (timeFraction > 0) {
            const totalSeconds = Math.round(timeFraction * 86400);
            date.setHours(Math.floor(totalSeconds / 3600), Math.floor((totalSeconds % 3600) / 60), totalSeconds % 60, 0);
        }
        return date;
    }

    if (typeof value === 'string') {
        const s = value.trim();
        if (!s) return null;

        const sep = '[\\/\\-.]';
        // Improved Regex: Supports Date + Optional Time (HH:mm:ss) + Optional AM/PM + Optional Offset (+04:00)
        const dateWithTimeRegex = new RegExp(`^(\\d{1,2})${sep}(\\d{1,2})${sep}(\\d{2,4})(?:\\s+(\\d{1,2})[:](\\d{1,2})(?:[:](\\d{1,2}))?)?(?:\\s*(AM|PM))?(?:\\s*[+-]\\d{2}:\\d{2})?`, 'i');
        const match = s.match(dateWithTimeRegex);

        if (match) {
            let [, p1, p2, p3, hourStr, minStr, secStr, period] = match;
            let val1 = parseInt(p1);
            let val2 = parseInt(p2);
            let year = parseInt(p3);
            if (p3.length === 2) year = year < 50 ? 2000 + year : 1900 + year;

            let hour = parseInt(hourStr || '0');
            let min = parseInt(minStr || '0');
            let sec = parseInt(secStr || '0');

            if (period) {
                if (period.toUpperCase() === 'PM' && hour < 12) hour += 12;
                if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;
            }

            let month, day;
            const hint = monthHint ? parseInt(monthHint) : null;

            // Priority Logic:
            // 1. If one value is > 12, the other MUST be the month.
            // 2. If monthHint is provided and matches one of the values, prioritize that.
            // 3. Otherwise, default to DD/MM (as per project region/contract style).
            
            if (val1 > 12) {
                day = val1; month = val2;
            } else if (val2 > 12) {
                day = val2; month = val1;
            } else if (hint === val1 && hint !== val2) {
                month = val1; day = val2;
            } else if (hint === val2 && hint !== val1) {
                day = val1; month = val2;
            } else if (monthHint === 'DD/MM') {
                day = val1; month = val2;
            } else if (monthHint === 'MM/DD') {
                month = val1; day = val2;
            } else {
                // DEFAULT TO DD/MM for this project/archive data
                day = val1; month = val2;
            }

            const d = new Date(year, month - 1, day, hour, min, sec);
            if (!isNaN(d.getTime())) return d;
        }

        const native = new Date(s);
        if (!isNaN(native.getTime())) return native;
    }
    return null;
}

export function toISOLocalDate(date) {
    if (!date || !(date instanceof Date)) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function toISOLocalDateTime(date) {
    if (!date || !(date instanceof Date)) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}:${s}`;
}

export function formatDate(value) {
    const date = parseExcelDate(value);
    if (!date) return value;
    // Format as DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

export function formatDateTime(value) {
    if (typeof value === 'string') return value;
    const date = parseExcelDate(value);
    if (!date) return value;
    // Format date and time
    const dateStr = date.toLocaleDateString('en-GB');
    const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${dateStr} ${timeStr}`;
}

export function formatDateLong(value) {
    const date = parseExcelDate(value);
    if (!date) return value?.toString() || '';

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
}

export function formatTimeOnly(timeValue) {
    if (!timeValue) return '';

    // If it's a number (Excel time fraction)
    if (typeof timeValue === 'number') {
        const totalSeconds = Math.round(timeValue * 86400);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const hStr = hours.toString().padStart(2, '0');
        const mStr = minutes.toString().padStart(2, '0');
        return seconds > 0 ? `${hStr}:${mStr}:${seconds.toString().padStart(2, '0')}` : `${hStr}:${mStr}`;
    }

    if (typeof timeValue === 'string') {
        const trimmed = timeValue.trim();

        // If it's already a nice format like "9:56 PM" or "21:56:00" or "09:56", keep it!
        if (/^\d{1,2}:\d{2}(?::\d{2})?(\s*(am|pm))?$/i.test(trimmed)) {
            return trimmed;
        }

        const time = trimmed.toLowerCase();

        // Handle AM/PM format
        if (time.includes('am') || time.includes('pm')) {
            const timePartsMatch = time.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)/i);
            if (timePartsMatch) {
                const [, hours, minutes, seconds, period] = timePartsMatch;
                return `${hours}:${minutes}${seconds ? ':' + seconds : ''} ${period.toUpperCase()}`;
            }
        }

        // Handle 24-hour format with potential seconds
        const match = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (match) {
            const [, hours, minutes, seconds] = match;
            return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}${seconds ? ':' + seconds : ''}`;
        }
    }

    return timeValue.toString();
}

/* ------------------------------------------------------------------------------------------
 * formatContractDateTime - the ONE display format for a contract's pick-up / drop-off
 * (start / end / return) date, everywhere it is SHOWN (owner, 27 Sep 2026):
 *   "dd/mm/yyyy HH:MM"  (24-hour, Dubai time) when the value carries a time,
 *   "dd/mm/yyyy"        when it carries none - a time is never invented,
 *   ''                  for an empty or unreadable value (the caller picks its own "—").
 * DISPLAY ONLY: it never changes a stored value, a sort, a filter or date maths.
 *
 * Accepts:
 *   - text "dd/mm/yyyy[ HH:MM[:SS]]" (also "-" or "." between the parts; a value whose 2nd
 *     part is > 12 is read the other way round, like parseExcelDate). AM/PM text is Ejar's old
 *     airport export, "M/D/YYYY h:mm:ss AM|PM [+04:00]": month first (kpi.logic.parseContractDate).
 *     The text is Ejar's own Dubai wall clock, so its time is shown exactly as written
 *     (seconds dropped), "00:00" included - also when it carries a zone.
 *   - text "yyyy-mm-dd" / "yyyy/mm/dd" (date only - never shifted by a time zone) and ISO
 *     "yyyy-mm-ddTHH:MM[:SS[.sss]][Z|+hh:mm]": with a zone it is an instant, shown in Dubai
 *     time (UTC -> +04:00); without one it is read as the Dubai wall clock.
 *   - a Date, a Firestore Timestamp (toDate() or {seconds} / {_seconds}), epoch milliseconds,
 *     or an Excel serial number (whole = date only, fraction = time).
 * A machine instant (Date, Timestamp, epoch, ISO with a zone, Excel serial) that lands exactly
 * on midnight (Dubai midnight, or UTC midnight for an instant) is how a date-only value is
 * stored, so it is shown without a time. Text that writes a wall-clock time without a zone
 * ("yyyy-mm-dd HH:MM", "dd/mm/yyyy HH:MM") always shows it, "00:00" included.
 * Dubai has no daylight saving: Dubai time = UTC + 4h, so no Intl / ICU is needed (this file
 * is also loaded by the Node e-mail automation - keep it free of imports).
 * ---------------------------------------------------------------------------------------- */
const DUBAI_OFFSET_MS = 4 * 3600 * 1000;
const pad2 = (n) => String(n).padStart(2, '0');

const validYMD = (y, m, d) => {
    if (!(y >= 1900 && y <= 9999) || !(m >= 1 && m <= 12) || !(d >= 1)) return false;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();   // days in that month
    return d <= last;
};

// parts -> display text; hh/mi null = no time
const dmyText = (y, m, d, hh, mi) => {
    if (!validYMD(y, m, d)) return '';
    const day = `${pad2(d)}/${pad2(m)}/${y}`;
    if (hh === null || hh === undefined) return day;
    if (!(hh >= 0 && hh <= 23) || !(mi >= 0 && mi <= 59)) return '';
    return `${day} ${pad2(hh)}:${pad2(mi)}`;
};

// an instant (epoch ms) -> Dubai wall clock.
// A date-only value is stored as an instant at midnight: Dubai midnight (new Date(y, m, d) in
// a Dubai browser) or UTC midnight (new Date('yyyy-mm-dd'), toISOString()). Both are shown
// as the date alone - never as an invented "00:00" / "04:00".
// ``written``: the value is text that WRITES its time (with its zone) - that time is always shown.
const DAY_MS = 86400000;
const instantText = (ms, written = false) => {
    if (typeof ms !== 'number' || !Number.isFinite(ms)) return '';
    const t = Math.floor(ms / 1000) * 1000;                    // drop milliseconds: a time is never rounded up
    const w = new Date(t + DUBAI_OFFSET_MS);
    if (Number.isNaN(w.getTime())) return '';
    const mod = (n) => ((n % DAY_MS) + DAY_MS) % DAY_MS;
    const noTime = !written && (mod(t + DUBAI_OFFSET_MS) === 0 || mod(t) === 0);
    return dmyText(w.getUTCFullYear(), w.getUTCMonth() + 1, w.getUTCDate(),
        noTime ? null : w.getUTCHours(), noTime ? null : w.getUTCMinutes());
};

// 12-hour clock -> 24-hour
const to24 = (hh, period) => {
    if (!period) return hh;
    const p = period.toUpperCase();
    if (hh < 1 || hh > 12) return NaN;
    if (p === 'PM' && hh < 12) return hh + 12;
    if (p === 'AM' && hh === 12) return 0;
    return hh;
};

// "+04:00" / "+0400" / "Z" -> minutes east of UTC
const zoneMinutes = (z) => {
    if (!z) return null;
    if (z === 'Z' || z === 'z') return 0;
    const m = /^([+-])(\d{2}):?(\d{2})$/.exec(z);
    if (!m) return null;
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
};

const DMY_RE = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[\sT]+(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(AM|PM)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;
const YMD_RE = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})(?:[\sT]+(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(AM|PM)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;

const textDateTime = (raw) => {
    const s = raw.trim();
    if (!s) return '';
    let y, m, d, hh = null, mi = null, period, zone, machine;
    let x = DMY_RE.exec(s);
    if (x) {
        let a = Number(x[1]);
        let b = Number(x[2]);
        // AM/PM text is Ejar's old airport export, month first ("M/D/YYYY h:mm:ss AM +04:00" - 37,730 of
        // them, not one day-first: kpi.logic.parseContractDate); everything else is day first
        if (x[7]) [a, b] = [b, a];
        if (b > 12 && a <= 12) [a, b] = [b, a];                // written the other way round
        d = a; m = b; y = Number(x[3]);
        machine = false;
    } else if ((x = YMD_RE.exec(s))) {
        y = Number(x[1]); m = Number(x[2]); d = Number(x[3]);
        machine = true;
    } else {
        return '';
    }
    if (x[4] !== undefined) { hh = Number(x[4]); mi = Number(x[5]); }
    period = x[7];
    zone = x[8];
    if (hh !== null) {
        hh = to24(hh, period);
        if (!Number.isFinite(hh)) return '';
    }
    if (!validYMD(y, m, d)) return '';
    const zm = zoneMinutes(zone);
    if (zm !== null && hh !== null) {
        // an instant written with its zone -> Dubai time
        const ss = x[6] !== undefined ? Number(x[6]) : 0;
        if (!(hh <= 23 && mi <= 59 && ss <= 59)) return '';
        // (an ISO instant at midnight is a date-only Date written by toISOString; a human stamp's
        // time - the old airport export's "4:00:00 AM +04:00" - is always shown)
        return instantText(Date.UTC(y, m - 1, d, hh, mi, ss) - zm * 60000, !machine);
    }
    // a wall clock that writes its time shows it, 00:00 included - the same as the engine's
    // contract_dt_text (the engine writes Ejar's minute-precise times as "yyyy-mm-dd HH:MM[:SS]")
    return dmyText(y, m, d, hh, mi);
};

// Excel serial -> Dubai wall clock (a serial has no zone: it IS the wall clock)
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const serialText = (n) => {
    const secs = Math.round(n * 86400);                        // to the second (float noise)
    const w = new Date(EXCEL_EPOCH_MS + secs * 1000);
    const hh = w.getUTCHours();
    const mi = w.getUTCMinutes();
    const noTime = secs % 86400 === 0;
    return dmyText(w.getUTCFullYear(), w.getUTCMonth() + 1, w.getUTCDate(), noTime ? null : hh, noTime ? null : mi);
};

export function formatContractDateTime(value) {
    if (value === null || value === undefined || value === '') return '';
    try {
        if (typeof value === 'string') return textDateTime(value);
        if (value instanceof Date) return instantText(value.getTime());
        if (typeof value === 'number') {
            if (!Number.isFinite(value) || value <= 0) return '';
            if (value < 2958466) return serialText(value);        // Excel serial (up to 31/12/9999)
            if (value > 1e11) return instantText(value);            // epoch milliseconds
            return '';
        }
        if (typeof value === 'object') {
            if (typeof value.toDate === 'function') {               // Firestore Timestamp
                const dt = value.toDate();
                return dt instanceof Date ? instantText(dt.getTime()) : '';
            }
            const secs = typeof value.seconds === 'number' ? value.seconds
                : typeof value._seconds === 'number' ? value._seconds : null;
            if (secs !== null) {
                const ns = typeof value.nanoseconds === 'number' ? value.nanoseconds
                    : typeof value._nanoseconds === 'number' ? value._nanoseconds : 0;
                return instantText(secs * 1000 + Math.floor(ns / 1e6));
            }
        }
    } catch (e) {
        return '';
    }
    return '';
}

export function isDateInRange(dateValue, startDate, endDate) {
    const d = parseExcelDate(dateValue);
    if (!d) return true; // Include unknowns to avoid data loss
    
    // Normalize to local midnight for comparison if comparing only dates
    const checkTime = d.getTime();
    
    if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (checkTime < start.getTime()) return false;
    }
    
    if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (checkTime > end.getTime()) return false;
    }
    
    return true;
}
